export const ZERO_TELEOP_COMMAND = Object.freeze({
  x: 0,
  y: 0,
  yaw: 0,
});

const clampAxis = (value) => Math.max(-1, Math.min(1, value));

const roundAxis = (value) => {
  const rounded = Math.round(clampAxis(value) * 100) / 100;
  return Object.is(rounded, -0) ? 0 : rounded;
};

const finiteAxis = (value) => (
  typeof value === 'number' && Number.isFinite(value) ? value : 0
);

export const applyAxisDeadZone = (value, deadZone) => {
  const axis = clampAxis(finiteAxis(value));
  const normalizedDeadZone = Math.max(0, Math.min(0.99, finiteAxis(deadZone)));
  const magnitude = Math.abs(axis);

  if (magnitude <= normalizedDeadZone) return 0;

  return roundAxis(
    Math.sign(axis) * ((magnitude - normalizedDeadZone) / (1 - normalizedDeadZone))
  );
};

const normalizeMoveAxes = (horizontal, vertical, deadZone) => {
  const rawHorizontal = clampAxis(finiteAxis(horizontal));
  const rawVertical = clampAxis(finiteAxis(vertical));
  const rawMagnitude = Math.hypot(rawHorizontal, rawVertical);
  const normalizedDeadZone = Math.max(0, Math.min(0.99, finiteAxis(deadZone)));

  if (rawMagnitude <= normalizedDeadZone || rawMagnitude === 0) {
    return { x: 0, y: 0 };
  }

  const limitedMagnitude = Math.min(1, rawMagnitude);
  const adjustedMagnitude = (
    (limitedMagnitude - normalizedDeadZone) / (1 - normalizedDeadZone)
  );
  const normalizedHorizontal = (rawHorizontal / rawMagnitude) * adjustedMagnitude;
  const normalizedVertical = (rawVertical / rawMagnitude) * adjustedMagnitude;

  return {
    x: roundAxis(-normalizedVertical),
    y: roundAxis(-normalizedHorizontal),
  };
};

export const getMoveJoystickCommand = (
  displacementX,
  displacementY,
  radius,
  deadZonePixels
) => {
  if (!Number.isFinite(radius) || radius <= 0) {
    return { x: 0, y: 0 };
  }

  return normalizeMoveAxes(
    displacementX / radius,
    displacementY / radius,
    deadZonePixels / radius
  );
};

export const getTurnJoystickYaw = (
  displacementX,
  radius,
  deadZonePixels
) => {
  if (!Number.isFinite(radius) || radius <= 0) return 0;

  return applyAxisDeadZone(
    -displacementX / radius,
    deadZonePixels / radius
  );
};

export const getGamepadTeleopCommand = (gamepad, deadZone) => {
  const getAxis = (index) => finiteAxis(gamepad?.axes?.[index]);
  const move = normalizeMoveAxes(getAxis(0), getAxis(1), deadZone);

  return {
    ...move,
    yaw: applyAxisDeadZone(-getAxis(2), deadZone),
  };
};

export const combineTeleopCommand = (move, yaw) => ({
  x: roundAxis(finiteAxis(move?.x)),
  y: roundAxis(finiteAxis(move?.y)),
  yaw: roundAxis(finiteAxis(yaw)),
});

export const isNeutralTeleopCommand = (command) => (
  finiteAxis(command?.x) === 0
  && finiteAxis(command?.y) === 0
  && finiteAxis(command?.yaw) === 0
);

export const buildRobotControlWebSocketUrl = (baseUrl, role, robotName) => {
  if (typeof baseUrl !== 'string' || baseUrl.trim() === '') {
    throw new Error('Robot control WebSocket base URL is required.');
  }
  if (!['controller', 'robot'].includes(role)) {
    throw new Error(`Unsupported robot control WebSocket role: ${role}`);
  }
  if (typeof robotName !== 'string' || robotName.trim() === '') {
    throw new Error('Robot name is required for the robot control WebSocket.');
  }

  return `${baseUrl.replace(/\/+$/, '')}/${role}/${encodeURIComponent(robotName)}/`;
};

export const createTeleopPayload = (robotName, command) => ({
  action: 'gotodirection',
  value: combineTeleopCommand(
    { x: command?.x, y: command?.y },
    command?.yaw
  ),
  robot_name: robotName,
  instruction: 'movement',
});
